/**
 * guestsV2Selectors — todo lo que Hoy, Reservas y el enlace del huésped saben
 * sobre las personas de una estancia, derivado en funciones puras.
 *
 * Reglas que se siguen aquí:
 *   1. Reserva ≠ huésped ≠ unidad física. Una reserva lleva N personas y cada
 *      persona es una fila con su propio `guest_id`.
 *   2. El pre-check-in se DERIVA de los datos que faltan, no de un
 *      interruptor. Lo obligatorio lo decide Ajustes, no este módulo.
 *   3. Las preferencias son opcionales y no bloquean NUNCA. Un huésped sin
 *      alergias no tiene un pre-check-in incompleto.
 *   4. El reporte de inmigración es un proceso aparte y nada lo deduce: ni el
 *      pre-check-in completo, ni la entrada, ni la salida.
 *   5. Del documento solo sale el ESTADO. Ni número ni imagen, nunca.
 *
 * Sin estado propio, sin efectos, sin red.
 */
import {
  GUESTS, PRE_CHECKIN_SUBMISSIONS, IMMIGRATION_CASES, GUEST_FIELDS,
  OPTIONAL_FIELDS, DEMO_GUEST_LINKS, PRE_CHECKIN_STEPS, TRANSFER_STATUS_LABEL,
  arrivalModeOf,
} from "@/data/guestsV2Demo";

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

/**
 * Las personas de una reserva: la semilla más lo que se haya añadido o
 * corregido en la sesión. El huésped principal va primero.
 */
export function guestsForReservation(reservationId, storeState) {
  const patches = storeState?.guestPatches || {};
  const seeded = GUESTS
    .filter((g) => g.reservation_id === reservationId)
    .map((g) => ({ ...g, ...(patches[g.guest_id] || {}) }));

  const sessionOnly = (storeState?.sessionGuests || [])
    .map((id) => patches[id])
    .filter((g) => g && g.reservation_id === reservationId
      && !seeded.some((s) => s.guest_id === g.guest_id));

  return [...seeded, ...sessionOnly]
    .sort((a, b) => Number(Boolean(b.primary_guest)) - Number(Boolean(a.primary_guest)));
}

export function primaryGuestOf(guests) {
  return guests.find((g) => g.primary_guest) || guests[0] || null;
}

/**
 * Composición del grupo. `declared` son las personas con nombre; `expected`
 * sale de la reserva (adultos + niños). Si hay menos fichas que plazas, el
 * resto son acompañantes sin registrar — se dice, no se inventan.
 */
export function partyOf(reservation, guests) {
  const adults = reservation?.guest?.adults || 0;
  const children = reservation?.guest?.children || 0;
  const expected = adults + children;
  return {
    declared: guests.length,
    expected,
    adults,
    children,
    unnamed: Math.max(0, expected - guests.length),
  };
}

// ---------------------------------------------------------------------------
// Qué es obligatorio (lo dice Ajustes, no este módulo)
// ---------------------------------------------------------------------------

/**
 * Campos obligatorios según Ajustes › Requisitos del huésped, separados por
 * ámbito: los que se piden a cada persona y los que se piden una vez.
 *
 * `flight_number` es un caso aparte: aunque la propiedad lo marque, solo se
 * exige si el huésped dice que llega en avión. Pedir un número de vuelo a
 * quien viene en barco sería un campo imposible de completar.
 */
export function requiredFieldsFor(settings) {
  const flags = settings?.guests?.fields || {};
  const active = Object.keys(GUEST_FIELDS).filter((f) => flags[f]);
  return {
    person: active.filter((f) => GUEST_FIELDS[f].scope === "person"),
    party: active.filter((f) => GUEST_FIELDS[f].scope === "party"),
    all: active,
  };
}

/** ¿Tiene esta persona el campo relleno? Del documento vale el ESTADO. */
export function hasPersonField(guest, field) {
  if (!guest) return false;
  if (field === "passport") {
    return (guest.document?.status || "not_provided") !== "not_provided";
  }
  const v = guest[field];
  return v !== null && v !== undefined && String(v).trim() !== "";
}

/**
 * Lo que el pre-check-in puede decir del documento, y nada más.
 *
 * Marcar una casilla NO es aportar un archivo. En esta vista previa no hay
 * subida de ficheros, así que lo máximo que el huésped puede hacer es CONFIRMAR
 * que trae su documento: eso es `declared`. Si esta función devolviera
 * `provided`, recepción leería "archivo aportado" en una pantalla donde no
 * existe ningún archivo, iría a buscarlo y no lo encontraría.
 *
 * Lo que ya comprobó recepción (`verified`) no se degrada por reenviar el
 * formulario: una comprobación en persona no la deshace una casilla.
 */
export function declaredDocumentState(current, confirmed) {
  const status = current?.status || "not_provided";
  if (!confirmed) return { status: "not_provided", type: null };
  if (status === "verified" || status === "provided") {
    return { status, type: current?.type || null };
  }
  return { status: "declared", type: current?.type || "passport" };
}

function hasPartyField(field, { primary, arrival }) {
  if (field === "arrival_time") {
    return Boolean(arrival?.estimated_arrival || arrival?.landing_time);
  }
  if (field === "flight_number") {
    // Solo exigible si llega en avión; si no, no aplica.
    if (arrival?.mode !== "plane") return true;
    return Boolean(String(arrival?.reference || "").trim());
  }
  const v = primary?.[field];
  return v !== null && v !== undefined && String(v).trim() !== "";
}

/**
 * Qué falta para dar el pre-check-in por completo. Devuelve una lista de
 * huecos concretos, no un número suelto, para que la pantalla pueda decir
 * exactamente qué pedir.
 */
export function missingFor({ guests, required, arrival }) {
  const primary = primaryGuestOf(guests);
  const out = [];

  for (const field of required.party) {
    if (!hasPartyField(field, { primary, arrival })) {
      out.push({ scope: "party", field, guest_id: null, guest_name: null });
    }
  }
  for (const guest of guests) {
    for (const field of required.person) {
      if (!hasPersonField(guest, field)) {
        out.push({
          scope: "person", field,
          guest_id: guest.guest_id, guest_name: guest.full_name,
        });
      }
    }
  }
  return out;
}

/** El paso del formulario donde se rellena un campo. */
export function stepForField(field) {
  return GUEST_FIELDS[field]?.step || PRE_CHECKIN_STEPS[0].key;
}

/**
 * El primer paso, en el orden del formulario, que tiene algo obligatorio
 * pendiente.
 *
 * Un formulario que dice "falta algo" sin llevarte a donde se arregla es un
 * callejón sin salida, sobre todo en móvil: el huésped está en el último paso y
 * lo que falta está dos pantallas atrás.
 */
export function firstIncompleteStep(missing) {
  if (!missing || missing.length === 0) return null;
  const keys = PRE_CHECKIN_STEPS.map((s) => s.key);
  let best = null;
  for (const m of missing) {
    const i = keys.indexOf(stepForField(m.field));
    if (i >= 0 && (best === null || i < best)) best = i;
  }
  return best === null ? null : keys[best];
}

/**
 * Estado del pre-check-in, DERIVADO.
 *
 *   sin envío y sin nada relleno       → not_started
 *   algo relleno pero falta algo       → in_progress
 *   nada obligatorio pendiente         → completed
 *   además comprobado por recepción    → verified
 *
 * Las preferencias no entran en esta cuenta: son opcionales por definición
 * (`OPTIONAL_FIELDS`), así que ni completan ni bloquean.
 */
export function preCheckinStatusFor({ guests, required, arrival, submission }) {
  const missing = missingFor({ guests, required, arrival });
  if (missing.length === 0 && required.all.length > 0) {
    const docsRequired = required.person.includes("passport");
    const allVerified = docsRequired
      && guests.length > 0
      && guests.every((g) => (g.document?.status || "not_provided") === "verified");
    return allVerified ? "verified" : "completed";
  }
  if (missing.length === 0) return "completed";

  // "Empezado" significa que ALGUIEN aportó algo, no que sepamos el nombre del
  // titular. Ese nombre llega con la reserva —del canal, de la OTA— sin que el
  // huésped haya tocado el formulario, así que no cuenta: si contara, toda
  // reserva nacería "a medias" y `not_started` no existiría nunca, que es
  // justo el estado que distingue "no ha empezado" de "lo dejó a medias"
  // (§7.4).
  const started = Boolean(submission?.submitted_at)
    || guests.some((g) => !g.from_reservation
      && required.person.some((f) => hasPersonField(g, f)));
  return started ? "in_progress" : "not_started";
}

// ---------------------------------------------------------------------------
// Inmigración — proceso de la propiedad, independiente de todo lo anterior
// ---------------------------------------------------------------------------

/**
 * Estado del reporte de extranjeros.
 *
 * Nada de esta función mira `stay_status` ni el estado del pre-check-in. Un
 * huésped puede llevar tres noches dentro con el reporte sin presentar, y un
 * pre-check-in impecable no presenta nada. Solo un dato explícito —declarado o
 * puesto por la propiedad en la sesión— dice `filed`.
 */
export function immigrationFor(reservationId, settings, storeState) {
  const on = Boolean(settings?.guests?.immigration_reporting_required);
  if (!on) return { state: "not_required", tracked: false, declared: false, filed_at: null, actor: null };

  const declared = storeState?.immigration?.[reservationId]
    || IMMIGRATION_CASES[reservationId]
    || null;

  if (declared?.state === "filed") {
    return {
      state: "filed", tracked: true, declared: true,
      filed_at: declared.filed_at || null, actor: declared.actor || null,
    };
  }
  // Sin constancia de presentación. Pendiente, nunca por deducción.
  return {
    state: "due", tracked: true, declared: Boolean(declared),
    filed_at: null, actor: null,
  };
}

// ---------------------------------------------------------------------------
// Llegada y preferencias
// ---------------------------------------------------------------------------

export function arrivalFor(reservationId, storeState) {
  const s = storeState?.submissions?.[reservationId] || PRE_CHECKIN_SUBMISSIONS[reservationId];
  return s?.arrival || null;
}

/** ¿Se le llega a preguntar por el traslado a quien llega así? */
export function asksTransfer(arrival) {
  return Boolean(arrivalModeOf(arrival?.mode).transfer_label);
}

/**
 * El traslado, para el equipo. Devuelve `null` cuando la pregunta NO se hace.
 *
 * A quien llega por su cuenta no se le ofrece traslado —no hay punto de
 * recogida, ni traslado genérico en el catálogo—, así que su ficha no puede
 * decir "sin indicar": eso pondría a recepción a perseguir una respuesta que
 * nadie pidió.
 */
export function transferSummaryOf(arrival) {
  if (!asksTransfer(arrival)) return null;
  return TRANSFER_STATUS_LABEL[arrival?.transfer || "none"] || null;
}

/**
 * Limpia lo que deja de tener sentido al cambiar de forma de llegada.
 *
 * Un número de vuelo no sobrevive a "vengo en barco", y un traslado desde el
 * aeropuerto no sobrevive a "llego por mi cuenta". Arrastrar esos valores
 * escondidos hace que el equipo prepare un coche para un vuelo que ya no
 * existe.
 */
export function arrivalForMode(arrival, mode) {
  const next = arrivalModeOf(mode);
  return {
    ...arrival,
    mode: next.value,
    reference: "",
    landing_time: "",
    transfer: next.transfer_label ? (arrival?.transfer || "") : "",
  };
}

export function preferencesFor(reservationId, storeState) {
  const s = storeState?.submissions?.[reservationId] || PRE_CHECKIN_SUBMISSIONS[reservationId];
  return s?.preferences || null;
}

export function submissionFor(reservationId, storeState) {
  return storeState?.submissions?.[reservationId] || PRE_CHECKIN_SUBMISSIONS[reservationId] || null;
}

/** Preferencias que operaciones tiene que ver aunque sean opcionales. */
export function operationalNotesOf(preferences) {
  if (!preferences) return [];
  const out = [];
  if (String(preferences.allergies || "").trim()) {
    out.push({ key: "allergies", label: "Alergias", value: preferences.allergies, tone: "red" });
  }
  if (String(preferences.dietary || "").trim()) {
    out.push({ key: "dietary", label: "Dieta", value: preferences.dietary, tone: "amber" });
  }
  if (preferences.minibar === "alcohol_free") {
    out.push({ key: "minibar", label: "Minibar", value: "Sin alcohol", tone: "blue" });
  }
  if (preferences.scooter_interest) {
    out.push({ key: "scooter", label: "Scooter", value: "Le interesa alquilar", tone: "stone" });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Composición — la vista única que consumen Hoy, Reservas y el huésped
// ---------------------------------------------------------------------------

/**
 * Todo lo que se sabe de las personas de una reserva.
 *
 * Es la ÚNICA fuente: Hoy y Reservas leen esto, así que la fila de Hoy y la
 * ficha de la reserva no pueden contradecirse.
 */
/**
 * Huésped mínimo a partir de la propia reserva.
 *
 * Una reserva SIEMPRE declara a quien reservó. Si todavía no hay fichas de
 * persona —porque nadie ha hecho el pre-check-in—, se usa ese nombre, que es
 * un dato real de la reserva, y lo demás queda vacío: sin nacionalidad, sin
 * documento, sin contacto. No se inventa nada; solo se deja de fingir que no
 * hay nadie en una reserva que tiene titular.
 */
function guestFromReservation(reservation) {
  const name = reservation?.guest?.name;
  if (!name) return [];
  return [{
    guest_id: `${reservation.reservation_id}-titular`,
    reservation_id: reservation.reservation_id,
    primary_guest: true,
    full_name: name,
    nationality: null,
    date_of_birth: null,
    document: { status: "not_provided", type: null },
    email: null,
    phone: null,
    from_reservation: true,
  }];
}

/**
 * Contacto de la reserva: el del HUÉSPED PRINCIPAL, y de un solo sitio.
 *
 * Ésta es la fuente autorizada del email y el teléfono en todas las superficies
 * V2. Antes el mismo dato se declaraba dos veces —en el modelo de huésped y en
 * la reserva— y las dos copias podían discrepar sin que nada lo notara;
 * `guestContactFor()` (Reservas) deriva de aquí para lo que todavía lo consume.
 *
 * Lo que no consta sigue sin constar: nada se compone a partir del nombre. La
 * nacionalidad sí puede caer al país declarado en la propia reserva, que es un
 * dato real de la reserva y no una invención.
 */
export function contactOf(guests, reservation = null) {
  const primary = primaryGuestOf(guests);
  return {
    email: primary?.email || null,
    phone: primary?.phone || null,
    nationality: primary?.nationality || reservation?.guest?.country || null,
    // `from_reservation` marca al titular derivado del nombre de la reserva:
    // ése no aporta contacto, solo evita fingir que no hay nadie.
    declared: Boolean(primary && !primary.from_reservation),
  };
}

export function buildGuestParty({ reservation, settings, storeState }) {
  const id = reservation?.reservation_id;
  const declared = guestsForReservation(id, storeState);
  const guests = declared.length > 0 ? declared : guestFromReservation(reservation);
  const required = requiredFieldsFor(settings);
  const arrival = arrivalFor(id, storeState);
  const preferences = preferencesFor(id, storeState);
  const submission = submissionFor(id, storeState);
  const missing = missingFor({ guests, required, arrival });
  const preCheckin = preCheckinStatusFor({ guests, required, arrival, submission });
  const immigration = immigrationFor(id, settings, storeState);

  // "Lista para la llegada" = nada obligatorio pendiente Y, si la propiedad
  // sigue el reporte, que además esté presentado. Son dos condiciones de dos
  // procesos distintos; se comprueban por separado y se muestran por separado.
  const ready = missing.length === 0
    && (preCheckin === "completed" || preCheckin === "verified")
    && (!immigration.tracked || immigration.state !== "due");

  return {
    reservation_id: id,
    reservation,
    guests,
    primary: primaryGuestOf(guests),
    contact: contactOf(guests, reservation),
    party: partyOf(reservation, guests),
    required,
    missing,
    pre_checkin: preCheckin,
    submitted_at: submission?.submitted_at || null,
    immigration,
    arrival,
    preferences,
    operational_notes: operationalNotesOf(preferences),
    ready,
  };
}

/**
 * Una frase con el estado del grupo, para cabeceras y resúmenes.
 * "3 huéspedes · Pre-check-in completado · APOA pendiente"
 */
export function partySummaryLine(view, { withCount = true } = {}) {
  if (!view) return "";
  const parts = [];
  if (withCount) {
    const n = view.party.declared;
    parts.push(n === 1 ? "1 huésped" : `${n} huéspedes`);
  }
  if (view.missing.length > 0) {
    parts.push(view.missing.length === 1
      ? "Falta 1 dato"
      : `Faltan ${view.missing.length} datos`);
  } else {
    parts.push("Pre-check-in completado");
  }
  if (view.arrival?.estimated_arrival) parts.push(`Llegada ${view.arrival.estimated_arrival}`);
  if (view.immigration.tracked) {
    parts.push(view.immigration.state === "filed" ? "APOA presentado" : "APOA pendiente");
  }
  return parts.join(" · ");
}

/** La frase corta de Hoy: solo lo que falta, sin repetir el nombre. */
export function readinessLine(view) {
  if (!view) return "";
  if (view.ready) return "Lista para la llegada";
  const parts = [];
  if (view.missing.length > 0) {
    parts.push(view.missing.length === 1 ? "1 dato pendiente" : `${view.missing.length} datos pendientes`);
  }
  if (view.immigration.tracked && view.immigration.state === "due") {
    parts.push("Inmigración pendiente");
  }
  return parts.join(" · ") || "Lista para la llegada";
}

// ---------------------------------------------------------------------------
// Enlace del huésped (demostración)
// ---------------------------------------------------------------------------

/**
 * Resuelve el token del enlace a una reserva.
 *
 * LÍMITE DE LA VISTA PREVIA: esto es una tabla en memoria. El token real irá
 * hasheado, atado a una reserva, con caducidad y revocación, y lo resolverá el
 * backend (§7.3.1). Aquí no autentica nada.
 */
export function reservationIdForToken(token) {
  return DEMO_GUEST_LINKS[String(token || "").trim()] || null;
}

export function tokenForReservation(reservationId) {
  const hit = Object.entries(DEMO_GUEST_LINKS).find(([, id]) => id === reservationId);
  return hit ? hit[0] : null;
}

export { OPTIONAL_FIELDS };
